/**
 * TypeScript mirror of `sshare-proto`.
 *
 * Hand-written rather than generated, because there are nine messages and a
 * generator would be more machinery than the protocol is worth. The rule that
 * keeps it honest: `crates/sshare-proto/src/message.rs` has tests that pin the
 * exact JSON for one variant of each direction, and the shapes below must match
 * those tests. If a Rust message changes, those tests fail before the browser
 * finds out.
 */

/** Must match `sshare_proto::PROTOCOL_VERSION`. */
export const PROTOCOL_VERSION = 1

/** Must match `sshare_proto::MAX_VIEWER_CAP`. */
export const MAX_VIEWER_CAP = 16

/** Room code length, from `sshare_proto::ROOM_CODE_LEN`. */
export const ROOM_CODE_LEN = 6

export type PeerRole = 'host' | 'viewer'

export interface PeerInfo {
  id: string
  name: string
  role: PeerRole
}

/** A STUN or TURN server. TURN entries carry time-limited credentials. */
export interface IceServer {
  urls: string[]
  username?: string
  credential?: string
}

/**
 * An SDP offer or answer.
 *
 * The field is `sdp_type`, not the browser's usual `type`, because this object
 * nests inside a `type`-tagged enum on the wire.
 */
export interface SessionDescription {
  sdp_type: 'offer' | 'answer'
  sdp: string
}

export interface IceCandidate {
  candidate: string
  sdp_mid?: string
  sdp_mline_index?: number
}

export type SignalPayload =
  | { type: 'keyframe_request' }
  | ({ type: 'offer' } & SessionDescription)
  | ({ type: 'answer' } & SessionDescription)
  | ({ type: 'ice_candidate' } & IceCandidate)

/** What a mark draws; see `sshare_proto::MarkKind`. */
export type MarkKind = 'ping' | 'pointer' | 'ink'

export type ErrorCode =
  | 'bad_request'
  | 'protocol_mismatch'
  | 'room_not_found'
  | 'bad_password'
  | 'room_full'
  | 'not_permitted'
  | 'rate_limited'
  | 'internal'

export type ClientMessage =
  | {
      type: 'host_create_room'
      relay?: boolean
      access_token?: string
      password?: string
      viewer_cap: number
      name: string
      protocol_version: number
    }
  | {
      type: 'viewer_join'
      code: string
      password?: string
      name: string
      protocol_version: number
    }
  | { type: 'signal'; to: string; payload: SignalPayload }
  | { type: 'relay_chat'; text: string }
  | { type: 'mark'; kind: MarkKind; stroke: number; points: [number, number][] }
  | { type: 'leave_room' }
  | { type: 'ping'; nonce: number }

export type ServerMessage =
  | {
      type: 'room_created'
      code: string
      peer_id: string
      ice_servers: IceServer[]
      viewer_cap: number
    }
  | {
      type: 'room_joined'
      peer: PeerInfo
      host: PeerInfo
      peers: PeerInfo[]
      ice_servers: IceServer[]
    }
  | { type: 'peer_joined'; peer: PeerInfo }
  | { type: 'peer_left'; peer_id: string }
  | { type: 'signal'; from: string; payload: SignalPayload }
  | { type: 'chat'; from: string; name: string; text: string; ts_ms: number }
  | { type: 'mark'; from: string; kind: MarkKind; stroke: number; points: [number, number][] }
  | { type: 'room_closed'; reason: string }
  | { type: 'pong'; nonce: number }
  | { type: 'error'; code: ErrorCode; message: string }

/**
 * Normalise a room code typed or pasted by a user.
 *
 * Mirrors `sshare_proto::normalize_room_code`: mixed case and separators are
 * tolerated, but a character that is alphanumeric yet outside the alphabet is
 * rejected instead of guessed at, because silently rewriting `0` to `O` would
 * send someone to a different room.
 */
export function normalizeRoomCode(input: string): string | null {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (const ch of input) {
    const up = ch.toUpperCase()
    if (alphabet.includes(up)) {
      out += up
    } else if (/[A-Za-z0-9]/.test(ch)) {
      return null
    }
    if (out.length > ROOM_CODE_LEN) return null
  }
  return out.length === ROOM_CODE_LEN ? out : null
}

/** Extract a room code from a full URL, a path, or a bare code. */
export function roomCodeFromInput(input: string): string | null {
  const direct = normalizeRoomCode(input)
  if (direct) return direct

  const match = /\/r\/([^/?#]+)/.exec(input)
  if (match) return normalizeRoomCode(match[1])

  return null
}

/** Human-readable text for a protocol error code. */
export function describeError(code: ErrorCode): string {
  switch (code) {
    case 'bad_request':
      return 'The server rejected the request.'
    case 'protocol_mismatch':
      return 'This page and the server disagree on the protocol version. Reload to pick up the current build.'
    case 'room_not_found':
      return 'That room does not exist. The host may have stopped streaming.'
    case 'bad_password':
      return 'Wrong password.'
    case 'room_full':
      return 'The room is full.'
    case 'not_permitted':
      return 'That action is not allowed in this room.'
    case 'rate_limited':
      return 'Too many attempts. Wait a moment and try again.'
    case 'internal':
      return 'The server hit an internal error.'
  }
}
